import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Progress,
  VStack,
  HStack,
  Flex,
} from '@chakra-ui/react';
import { getUsefulVars } from '../../handlers/helpers';
import { onLink } from '../../handlers';

const MyersBriggsTest = ({ props,onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({ E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 });
  const [showResults, setShowResults] = useState(false);

  const questions = [
    { text: "At a party, I prefer to interact with many people rather than a few close friends", dimension: "E-I", choice: "E" },
    { text: "I focus on concrete facts and details rather than big picture possibilities", dimension: "S-N", choice: "S" },
    { text: "I make decisions based on logic and objective analysis rather than personal values", dimension: "T-F", choice: "T" },
    { text: "I prefer to have things settled and organized rather than keeping options open", dimension: "J-P", choice: "J" },
    { text: "I feel energized after spending time with groups of people", dimension: "E-I", choice: "E" },
    { text: "I trust concrete experience and practical applications over theories", dimension: "S-N", choice: "S" },
    { text: "I value fairness and objective principles over personal harmony", dimension: "T-F", choice: "T" },
    { text: "I like to make plans well in advance rather than being spontaneous", dimension: "J-P", choice: "J" },
    { text: "I enjoy being the center of attention in social situations", dimension: "E-I", choice: "E" },
    { text: "I focus on what is real and actual rather than possibilities and potential", dimension: "S-N", choice: "S" },
    { text: "I prioritize truth and facts even if they might hurt someone's feelings", dimension: "T-F", choice: "T" },
    { text: "I feel most comfortable with structure and clear deadlines", dimension: "J-P", choice: "J" },
    { text: "I need quiet time alone to recharge my energy", dimension: "E-I", choice: "I" },
    { text: "I'm drawn to abstract concepts and future possibilities", dimension: "S-N", choice: "N" },
    { text: "I consider how decisions will affect people's feelings", dimension: "T-F", choice: "F" },
    { text: "I prefer to stay flexible and adapt as things unfold", dimension: "J-P", choice: "P" },
    { text: "I prefer deep conversations with a few people over small talk with many", dimension: "E-I", choice: "I" },
    { text: "I enjoy exploring theoretical concepts and innovative ideas", dimension: "S-N", choice: "N" },
    { text: "I value empathy and maintaining harmony in relationships", dimension: "T-F", choice: "F" },
    { text: "I like to keep my options open and decide at the last minute", dimension: "J-P", choice: "P" },
  ];

  const typeDescriptions = {
    "ESTJ": { name: "The Executive", description: "Organized, practical, and decisive. Natural leaders who value efficiency and results." },
    "ESTP": { name: "The Entrepreneur", description: "Energetic, pragmatic, and spontaneous. Thrive on action and solving problems in the moment." },
    "ESFJ": { name: "The Consul", description: "Caring, social, and organized. Focus on helping others and maintaining harmony." },
    "ESFP": { name: "The Entertainer", description: "Outgoing, spontaneous, and fun-loving. Bring energy and enthusiasm to everything." },
    "ENTJ": { name: "The Commander", description: "Strategic, logical, and assertive. Natural born leaders with long-term vision." },
    "ENTP": { name: "The Debater", description: "Innovative, clever, and curious. Love exploring ideas and challenging the status quo." },
    "ENFJ": { name: "The Protagonist", description: "Charismatic, inspiring, and empathetic. Natural motivators who help others grow." },
    "ENFP": { name: "The Campaigner", description: "Enthusiastic, creative, and sociable. See life as full of possibilities and potential." },
    "ISTJ": { name: "The Logistician", description: "Practical, responsible, and detail-oriented. Value tradition and clear procedures." },
    "ISTP": { name: "The Virtuoso", description: "Practical, observant, and adaptable. Skilled at understanding how things work." },
    "ISFJ": { name: "The Defender", description: "Caring, loyal, and thorough. Dedicated to protecting and supporting others." },
    "ISFP": { name: "The Adventurer", description: "Gentle, artistic, and spontaneous. Live in the moment and appreciate beauty." },
    "INTJ": { name: "The Architect", description: "Strategic, innovative, and independent. Focus on implementing long-term visions." },
    "INTP": { name: "The Logician", description: "Analytical, creative, and curious. Love understanding complex systems and theories." },
    "INFJ": { name: "The Advocate", description: "Idealistic, insightful, and principled. Seek deep meaning and authentic connections." },
    "INFP": { name: "The Mediator", description: "Idealistic, creative, and empathetic. Guided by values and seeking purpose." }
  };

  const dimensionInfo = {
    "E": { name: "Extraversion", description: "Energy from external world, people, and activities" },
    "I": { name: "Introversion", description: "Energy from inner world, reflection, and solitude" },
    "S": { name: "Sensing", description: "Focus on concrete facts, details, and practical reality" },
    "N": { name: "Intuition", description: "Focus on patterns, possibilities, and future potential" },
    "T": { name: "Thinking", description: "Decisions based on logic, objectivity, and analysis" },
    "F": { name: "Feeling", description: "Decisions based on values, empathy, and impact on people" },
    "J": { name: "Judging", description: "Prefer structure, planning, and organization" },
    "P": { name: "Perceiving", description: "Prefer flexibility, spontaneity, and adaptability" }
  };

  const handleAnswer = (rating) => {
    const question = questions[currentQuestion];
    const choice = question.choice;
    const opposite = getOpposite(choice);

    if (rating === 5) {
      setScores(prev => ({ ...prev, [choice]: prev[choice] + 2 }));
    } else if (rating === 3) {
      setScores(prev => ({ ...prev, [choice]: prev[choice] + 1 }));
    } else if (rating === 1) {
      // No points added
    } else if (rating === -1) {
      setScores(prev => ({ ...prev, [opposite]: prev[opposite] + 1 }));
    } else if (rating === -3) {
      setScores(prev => ({ ...prev, [opposite]: prev[opposite] + 2 }));
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getOpposite = (letter) => {
    const opposites = { E: 'I', I: 'E', S: 'N', N: 'S', T: 'F', F: 'T', J: 'P', P: 'J' };
    return opposites[letter];
  };

  const getPersonalityType = () => {
    let type = '';
    type += scores.E >= scores.I ? 'E' : 'I';
    type += scores.S >= scores.N ? 'S' : 'N';
    type += scores.T >= scores.F ? 'T' : 'F';
    type += scores.J >= scores.P ? 'J' : 'P';
    return type;
  };

  const getDimensionScores = () => {
    return [
      { dimension: 'E/I', e: scores.E, i: scores.I, preference: scores.E >= scores.I ? 'E' : 'I' },
      { dimension: 'S/N', s: scores.S, n: scores.N, preference: scores.S >= scores.N ? 'S' : 'N' },
      { dimension: 'T/F', t: scores.T, f: scores.F, preference: scores.T >= scores.F ? 'T' : 'F' },
      { dimension: 'J/P', j: scores.J, p: scores.P, preference: scores.J >= scores.P ? 'J' : 'P' }
    ];
  };

  const handleSaveAndContinue = () => {
    const personalityType = getPersonalityType();

    const uv = getUsefulVars(props);
    const resume = uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex];
    if(resume['new-aptitude'] === undefined) {
      resume['new-aptitude'] = [];
      resume['new-aptitude'].push({});
    }
    uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex]['new-aptitude'][0].myers_briggs = personalityType;

    onLink(false, props.formikProps, props.stateProps, 'new-aptitude',0,'section',props.stateProps.sectionData.resumeIndex);
  };

  const colors = {
    primary: '#252f59',
    secondary: '#dd1198',
    tertiary: '#ff9800'
  };

  if (showResults) {
    const personalityType = getPersonalityType();
    const dimensionScores = getDimensionScores();
    const typeInfo = typeDescriptions[personalityType];

    return (
      <Container maxW="container.lg" py={6}>
        <Box bg="white" p={6} borderRadius="lg" shadow="lg">
          <Heading as="h2" size="xl" textAlign="center" mb={6} color={colors.primary}>
            Your Personality Type Results
          </Heading>

          <Box
            borderLeftWidth="4px"
            borderColor={colors.secondary}
            bg="#fff5f7"
            p={4}
            mb={6}
            borderRadius="md"
          >
            <Text fontSize="lg" fontWeight="semibold" color={colors.primary}>
              Your Personality Type: <Text as="span" fontSize="2xl" color={colors.secondary}>{personalityType}</Text>
            </Text>
            <Text fontSize="md" mt={2} color={colors.tertiary}>
              <Text as="strong">{typeInfo.name}</Text>
            </Text>
          </Box>

          <Box mb={8} p={4} borderRadius="lg" bg="gray.50">
            <Text color="gray.700">{typeInfo.description}</Text>
          </Box>

          <VStack spacing={6} mb={8} align="stretch">
            <Heading as="h3" size="lg" color={colors.primary}>
              Your Personality Dimensions
            </Heading>

            {dimensionScores.map((dim, index) => {
              const letters = dim.dimension.split('/');
              const score1 = dim[letters[0].toLowerCase()];
              const score2 = dim[letters[1].toLowerCase()];
              const total = score1 + score2;
              const percentage1 = total > 0 ? (score1 / total) * 100 : 50;
              const percentage2 = total > 0 ? (score2 / total) * 100 : 50;
              const borderColor = index % 3 === 0 ? colors.primary : index % 3 === 1 ? colors.secondary : colors.tertiary;

              return (
                <Box
                  key={index}
                  borderLeftWidth="4px"
                  borderColor={borderColor}
                  bg="gray.50"
                  p={4}
                  borderRadius="md"
                >
                  <Flex justify="space-between" align="center" mb={2}>
                    <Heading as="h4" size="md" color={colors.primary}>
                      {dimensionInfo[letters[0]].name} vs {dimensionInfo[letters[1]].name}
                    </Heading>
                    <Text fontWeight="bold" fontSize="xl" color={dim.preference === letters[0] ? colors.secondary : colors.tertiary}>
                      {dim.preference}
                    </Text>
                  </Flex>

                  <Flex gap={2} mb={2}>
                    <Box flex="1">
                      <Box
                        h="6"
                        borderRadius="md"
                        bg={colors.secondary}
                        width={`${percentage1}%`}
                        minW="20%"
                      />
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        {letters[0]}: {score1} ({Math.round(percentage1)}%)
                      </Text>
                    </Box>
                    <Flex flex="1" direction="column" align="flex-end">
                      <Box
                        h="6"
                        borderRadius="md"
                        bg={colors.tertiary}
                        width={`${percentage2}%`}
                        minW="20%"
                      />
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        {letters[1]}: {score2} ({Math.round(percentage2)}%)
                      </Text>
                    </Flex>
                  </Flex>

                  <Box fontSize="sm" color="gray.600" mt={2}>
                    <Text>
                      <Text as="strong">{letters[0]}:</Text> {dimensionInfo[letters[0]].description}
                    </Text>
                    <Text>
                      <Text as="strong">{letters[1]}:</Text> {dimensionInfo[letters[1]].description}
                    </Text>
                  </Box>
                </Box>
              );
            })}
          </VStack>

          <Button
            onClick={() => {
              handleSaveAndContinue(props);
            }}
            w="full"
            mt={6}
            bg={colors.secondary}
            color="white"
            width='260px'
            py={6}
            fontSize="md"
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Save Results & Continue
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={6}>
      <Box bg="white" p={6} borderRadius="lg" shadow="lg">
        <Heading as="h1" size="xl" textAlign="center" mb={2} color={colors.primary}>
          Myers-Briggs Personality Test
        </Heading>
        <Text textAlign="center" color="gray.600" mb={6}>
          Discover your personality type. Answer honestly based on how you naturally are, not how you wish to be.
        </Text>

        <Box mb={6}>
          <Flex justify="space-between" fontSize="sm" color="gray.600" mb={2}>
            <Text>Question {currentQuestion + 1} of {questions.length}</Text>
            <Text>{Math.round(((currentQuestion) / questions.length) * 100)}%</Text>
          </Flex>
          <Progress
            value={((currentQuestion) / questions.length) * 100}
            size="sm"
            colorScheme="pink"
            bg="gray.200"
            borderRadius="full"
          />
        </Box>

        <Flex
          bg="gray.50"
          p={6}
          borderRadius="lg"
          mb={6}
          minH="32"
          align="center"
          justify="center"
        >
          <Text fontSize="xl" textAlign="center" fontWeight="semibold" color={colors.primary}>
            {questions[currentQuestion].text}
          </Text>
        </Flex>

        <VStack spacing={3}>
          <Button
            onClick={() => handleAnswer(5)}
            w="full"
            bg={colors.tertiary}
            color="white"
            width='160px'
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Agree
          </Button>
          <Button
            onClick={() => handleAnswer(3)}
            w="full"
            bg={colors.secondary}
            color="white"
            width='160px'
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Agree
          </Button>
          <Button
            onClick={() => handleAnswer(1)}
            w="full"
            bg={colors.primary}
            color="white"
            width='160px'
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Neutral
          </Button>
          <Button
            onClick={() => handleAnswer(-1)}
            w="full"
            bg="#6c757d"
            color="white"
            width='160px'
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Disagree
          </Button>
          <Button
            onClick={() => handleAnswer(-3)}
            w="full"
            bg="#e9ecef"
            color={colors.primary}
            width='160px'
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Disagree
          </Button>
        </VStack>
      </Box>
      <Button
      w="full"
      mt={6}
      bg="#dd1198"
      color="white"
      py={6}
      fontSize="md"
      width="260px"
      fontWeight="semibold"
      borderRadius="lg"
      _hover={{ opacity: 0.9 }}  className="aptitudeButton"
      onClick={() => onLink(false,props.formikProps,props.stateProps,'new-aptitude')}>
      Return
    </Button>
    </Container>
  );
};

export default MyersBriggsTest;